import React, { useCallback, useContext } from 'react'
import { useInView } from 'react-intersection-observer'
import { EmbeddedAttachmentsContext } from '@components/MessageAttachments/MessageAttachmentsContext'
import { TickerThreadContext } from '@components/MessageThread/TickerThreadContext'
import { MessageTimelineContext } from '@components/MessageTimeline/MessageTimelineContext'
import { useTradeAnalytics } from '@components/Web3/Trading/hooks/useTradeAnalytics'
import { TickerAttachment } from '@towns-protocol/sdk'
import { Box, Button, Paragraph, Stack, Text } from '@ui'
import { useDevice } from 'hooks/useDevice'
import { useOpenMessageThread } from 'hooks/useOpenThread'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { useSizeContext } from 'ui/hooks/useSizeContext'
import { shimmerClass } from 'ui/styles/globals/shimmer.css'

import { TickerHeader } from './TickerInfoBox'
import { TradingChart } from './TradingChart'
import { useCoinData } from './useCoinData'

export const Ticker = (props: {
    attachment: TickerAttachment
    eventId: string | undefined
    threadParentId: string | undefined
    messageCreatedAt?: number
}) => {
    if (props.threadParentId && props.threadParentId !== props.eventId) {
        return (
            <MinimalTicker address={props.attachment.address} chainId={props.attachment.chainId} />
        )
    }

    return <TradingChartTicker {...props} />
}

export const MinimalTicker = (props: {
    address: string
    chainId: string
    singleLine?: boolean
}) => {
    const { ref, inView } = useInView({
        rootMargin: '10px 0px',
    })
    const { address, chainId } = props

    const {
        data: coinData,
        isLoading,
        error,
    } = useCoinData({
        address,
        chain: chainId,
        disabled: !inView,
    })
    const { openPanel } = usePanelActions()

    if (props.singleLine) {
        return coinData?.token.symbol ? (
            <Box padding="xs" background="level3" rounded="xs" insetY="xxs">
                <Text strong>${coinData?.token.symbol}</Text>
            </Box>
        ) : null
    }

    return (
        <Box
            padding
            hoverable
            background="level2"
            rounded="md"
            className={isLoading ? shimmerClass : ''}
            minHeight="x8"
            cursor="pointer"
            ref={ref}
            onClick={() => {
                openPanel(CHANNEL_INFO_PARAMS.TRADE_PANEL, {
                    mode: 'buy',
                    tokenAddress: address,
                    chainId,
                })
            }}
        >
            {coinData ? (
                <TickerHeader minimal coinData={coinData} address={address} chainId={chainId} />
            ) : error ? (
                <Paragraph>Error loading coin data</Paragraph>
            ) : (
                <Paragraph color="gray1">Loading...</Paragraph>
            )}
        </Box>
    )
}

export const TradingChartTicker = (props: {
    attachment: TickerAttachment
    eventId: string | undefined
    messageCreatedAt?: number
}) => {
    const { attachment, eventId, messageCreatedAt } = props
    const { ref, inView } = useInView({
        rootMargin: '10px 0px',
    })
    const { openPanel } = usePanelActions()
    // a little workaround to cover the case where old tickers were posted
    // a temp chainId for solana
    const remappedChain =
        attachment.chainId === '1151111081099710' ? 'solana-mainnet' : attachment.chainId
    const { onOpenMessageThread } = useOpenMessageThread()

    const isTradeThreadContext = useContext(TickerThreadContext) !== undefined
    const timelineContext = useContext(MessageTimelineContext)

    const attachmentContext = useContext(EmbeddedAttachmentsContext)

    const { trackClickTokenMessage } = useTradeAnalytics({
        chainId: attachment.chainId,
        address: attachment.address,
    })

    const onTradeClick = useCallback(
        (mode: 'buy' | 'sell') => {
            // if the user is not in a space, open the trade panel
            // for now, we don't support threaded trading in DMs
            if (eventId && timelineContext?.spaceId) {
                onOpenMessageThread(eventId, {
                    mode,
                })
                trackClickTokenMessage({
                    tradeAction: mode,
                })
            } else {
                openPanel(CHANNEL_INFO_PARAMS.TRADE_PANEL, {
                    mode,
                    tokenAddress: attachment.address,
                    chainId: remappedChain,
                })
            }
        },
        [
            eventId,
            timelineContext?.spaceId,
            onOpenMessageThread,
            trackClickTokenMessage,
            openPanel,
            attachment.address,
            remappedChain,
        ],
    )

    const { containerWidth } = useSizeContext()

    const isTouch = useDevice()

    // prevent context menut to open when interacting with the component
    const onPreventTouchClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }, [])

    return (
        <Stack
            gap="paragraph"
            rounded="md"
            style={{
                width: 550,
                maxWidth: containerWidth - 80,
            }}
            background="level2"
            ref={ref}
            overflow="hidden"
            paddingBottom="md"
            onClick={isTouch ? onPreventTouchClick : undefined}
        >
            <TradingChart
                address={attachment.address}
                chainId={remappedChain}
                disabled={!inView}
                messageCreatedAt={messageCreatedAt}
            />

            {!isTradeThreadContext && !attachmentContext?.isMessageAttachementContext && (
                <Stack horizontal paddingX paddingY="none" gap="sm">
                    <Button
                        grow
                        size="button_x5"
                        tone="level3"
                        rounded="full"
                        color="cta1"
                        onClick={() => onTradeClick('buy')}
                    >
                        <Text>Buy</Text>
                    </Button>
                    <Button
                        grow
                        size="button_x5"
                        tone="level3"
                        rounded="full"
                        color="peach"
                        onClick={() => onTradeClick('sell')}
                    >
                        <Text>Sell</Text>
                    </Button>
                </Stack>
            )}
        </Stack>
    )
}
